import React from 'react'
import { StyleSheet, TouchableOpacity, View, Platform } from 'react-native'
import { Text, Layout, Icon, useTheme } from '@ui-kitten/components'
import Divider from './Divider'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { currencyFormatterAtom, currentStoreSaleSettingsAtom, priceTypesAtom } from '../global/AtomsProvider'
import { roundNumber } from '../utils/numberUtils'
import { resolveBonusLevel } from '../utils/bonusLevel'
import {
  totalCartPrice,
  totalCartPriceWithPriceModificators,
  totalCartPurchasePrice,
  discountPrice,
  priceModificatorsValue,
  bonusReductionValue,
  bonusAccumulationValue,
  punchCardReduction,
  finalTotalCartPrice,
  finalItemPrice,
  itemPriceWithoutModifiers,
  totalCartQuantity,
} from '../utils/cartPricing'

// The cart pricing math lives in utils/cartPricing.js so non-UI callers (the sales
// import) can use the exact same functions without pulling in React/UI Kitten.
// Re-exported here because ~all existing call sites import it from this module.
export {
  totalCartPrice,
  totalCartPriceWithPriceModificators,
  totalCartPurchasePrice,
  discountPrice,
  priceModificatorsValue,
  bonusReductionValue,
  bonusAccumulationValue,
  punchCardReduction,
  finalTotalCartPrice,
  finalItemPrice,
  itemPriceWithoutModifiers,
  totalCartQuantity,
}

export function CartViewBase( { sale, cartItems, discountPercentage, discountAmount, purchaseMethod, onItemPress, store, style, changeQuantityOfItem=null, client=null, bonusAction=null, cashbackEnabled=false, punchCardEnabled=false, punchCardCapacity=0, priceType=null } ) {
  const { t } = useTranslation()
  const theme = useTheme()
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const currentStoreSaleSettings = useAtomValue(currentStoreSaleSettingsAtom)
  const priceTypes = useAtomValue(priceTypesAtom)
  const effectivePriceModificators = sale?.priceModificators || purchaseMethod?.priceModificators
  
  return (
    <Layout style={[styles.cart, style]}>
    {
      cartItems?.map((item, index) => (
        <TouchableOpacity key={item.id+index} onPress={onItemPress ? () => onItemPress(item, index) : ()=> {}}>
          <View style={styles.cartItem}>
            {/* <Icon name='image-outline' fill='#000000' style = {styles.itemImage} /> */}
            <Text style={styles.itemTitle} category='c2'>
              {(item.isModifier ? '- ' : '') + item.name + (item.isReturned ? ` (${t('isReturned')})` : '')}
            </Text>
            <View style={styles.itemQuantityContainer}>
              { changeQuantityOfItem != null && item.quantity < 100 && 
                <TouchableOpacity onPress={(e) => {
                  e.target.blur()
                  if (e.keyCode === 13) {
                    if (Platform.OS === 'web') {
                      document.dispatchEvent(new KeyboardEvent('keydown', {'keyCode': 13, 'which': 13}))
                    }
                    return
                  }
                  changeQuantityOfItem(item.isModifier ? -item.modifierQuantity : -1, item, index)
                }} style={styles.changeButton}>
                  <View>
                    <Icon name='minus-circle-outline' fill={theme['text-hint-color']} height={25} width={25}  />
                  </View>
                </TouchableOpacity>
              }
              <Text 
                numberOfLines={1} 
                adjustsFontSizeToFit={true}
                minimumFontScale={0.5}
                style={styles.itemQuantityField} 
                category='c2'
              >
                {roundNumber(item.quantity)}
              </Text>
              { changeQuantityOfItem != null && item.quantity < 100 && 
                <TouchableOpacity onPress={(e) => {
                  e.target.blur()
                  if (e.keyCode === 13) {
                    if (Platform.OS === 'web') {
                      document.dispatchEvent(new KeyboardEvent('keydown', {'keyCode': 13, 'which': 13}))
                    }
                    return
                  }
                  changeQuantityOfItem(item.isModifier ? item.modifierQuantity : 1, item, index)
                }} style={styles.changeButton}>
                  <View>
                    <Icon name='plus-circle-outline' fill={theme['text-hint-color']} height={25} width={25}  />
                  </View>
                </TouchableOpacity>
              } 
            </View>
            <View style={styles.itemPriceContainer}>
              <Text 
                style={styles.itemPriceField} 
                numberOfLines={1} 
                adjustsFontSizeToFit={true}
                minimumFontScale={0.5}
                category='c2'
              >
                {currencyFormatter.format((item.itemPriceWithoutModifiers ? item.itemPriceWithoutModifiers : itemPriceWithoutModifiers(item, store, 0, priceType, priceTypes)))}
              </Text>
              {item.itemDiscount != null && (item.itemDiscount?.percentage || item.itemDiscount?.amount || 0) != 0 && (
                <Text style={styles.itemDiscountField} category='c1'>
                  {'(-' + item.itemDiscount.text + ')'}
                </Text>
              )}
            </View>
          </View>
        </TouchableOpacity>
      ))
    }
    {cartItems?.length > 0 && (
      <View style={styles.cartTotalView}>
        <Divider />
        { effectivePriceModificators && effectivePriceModificators.length > 0 && effectivePriceModificators.map (
          (item, index) => (
            <View style={styles.cartTotalRowView} key={index}>
              <Text style={styles.cartTotalPriceLabel}>
                {item.name}
              </Text>
              <Text style={styles.cartTotalPriceValue}>
                {currencyFormatter.format((sale ? sale.totalCartPrice : totalCartPrice(cartItems, store, priceType, priceTypes)) * (item.value/100))}
              </Text>
            </View>
          )
        )}
        { discountPercentage != null && discountPercentage != 0 && (
          <>
            <View style={styles.cartTotalRowView}>
              <Text style={styles.cartTotalPriceLabel}>
                {t('cartView.totalWithoutDiscount')}
              </Text>
              <Text style={styles.cartTotalPriceValue}>
                {currencyFormatter.format((sale ? sale.totalCartPriceWithPriceModificators : totalCartPriceWithPriceModificators(cartItems, store, effectivePriceModificators, priceType, priceTypes)))}
              </Text>
            </View>
            <View style={styles.cartTotalRowView}>
              <Text style={styles.cartTotalPriceLabel}>
                {t('cartView.discount')} {discountPercentage}%
              </Text>
              <Text style={styles.cartTotalPriceValue}>
                {currencyFormatter.format((sale ? (sale.discountPercentagePrice ?? sale.discountPrice) : discountPrice(discountPercentage, 0, cartItems, store, priceType, priceTypes)))}
              </Text>
            </View>
          </>
        )}
        { discountAmount != null && discountAmount != 0 && (
          <>
            <View style={styles.cartTotalRowView}>
              <Text style={styles.cartTotalPriceLabel}>
                {t('cartView.totalWithoutDiscount')}
              </Text>
              <Text style={styles.cartTotalPriceValue}>
                {currencyFormatter.format((sale ? sale.totalCartPriceWithPriceModificators : totalCartPriceWithPriceModificators(cartItems, store, effectivePriceModificators, priceType, priceTypes)))}
              </Text>
            </View>
            <View style={styles.cartTotalRowView}>
              <Text style={styles.cartTotalPriceLabel}>
                {t('cartView.discount')}
              </Text>
              <Text style={styles.cartTotalPriceValue}>
                {currencyFormatter.format(discountAmount)}
              </Text>
            </View>
          </>
        )}
        { client && bonusAction == 1 && cashbackEnabled && (
          <View style={styles.cartTotalRowView}>
            <Text style={styles.cartTotalPriceLabel}>
              {t('cartView.bonusReduction')}
            </Text>
            <Text style={styles.cartTotalPriceValue}>
              {currencyFormatter.format((sale ? sale.bonusReduction : bonusReductionValue(client, bonusAction, discountPercentage, discountAmount, cartItems, store, cashbackEnabled, effectivePriceModificators, priceType, priceTypes)))}
            </Text>
          </View>
        ) }
        { client && (sale?.punchCardReduction || punchCardReduction(client, discountPercentage, discountAmount, cartItems, store, punchCardEnabled, punchCardCapacity, effectivePriceModificators, priceType, priceTypes) > 0) && (
          <View style={styles.cartTotalRowView}>
            <Text style={styles.cartTotalPriceLabel}>
              {t('cartView.punchCardReduction')}
            </Text>
            <Text style={styles.cartTotalPriceValue}>
              {currencyFormatter.format((sale ? sale.punchCardReduction : punchCardReduction(client, discountPercentage, discountAmount, cartItems, store, punchCardEnabled, punchCardCapacity, effectivePriceModificators, priceType, priceTypes)))}
            </Text>
          </View>
        )}
        { currentStoreSaleSettings?.showTotalPurchasePriceInCart && (
          <View style={styles.cartTotalRowView}>
            <Text style={styles.cartTotalPriceLabel}>
              {t('cartView.totalPurchasePrice')}
            </Text>
            <Text style={styles.cartTotalPriceValue}>
              {currencyFormatter.format((sale ? sale.totalPurchasePrice : totalCartPurchasePrice(cartItems)))}
            </Text>
          </View>
        )}
        <View style={styles.cartTotalRowView}>
          <Text category='c2' style={styles.itemTitle}>
            {t('cartView.total')}
          </Text>
          <Text category='c2' style={[styles.itemQuantityContainer, styles.itemQuantityField]}>
            {roundNumber(sale ? sale.totalCartQuantity : totalCartQuantity(cartItems))}
          </Text>
          <Text category='c2' style={[styles.itemPriceContainer, styles.itemPriceField]}>
            {currencyFormatter.format((sale ? sale.finalTotalCartPrice : finalTotalCartPrice(discountPercentage, discountAmount, cartItems, store, client, bonusAction, cashbackEnabled, punchCardEnabled, punchCardCapacity, effectivePriceModificators, priceType, priceTypes)))}
          </Text>
        </View>
        { sale && sale.secondPaymentMethod && (
          <>
          <View style={styles.cartTotalRowView}>
            <Text style={styles.cartTotalPriceLabel}>
              {sale.paymentMethod.name}
            </Text>
            <Text style={styles.cartTotalPriceValue}>
              {currencyFormatter.format(sale.paymentMethod.value)}
            </Text>
          </View>
          <View style={styles.cartTotalRowView}>
            <Text style={styles.cartTotalPriceLabel}>
              {sale.secondPaymentMethod.name}
            </Text>
            <Text style={styles.cartTotalPriceValue}>
              {currencyFormatter.format(sale.secondPaymentMethod.value)}
            </Text>
          </View>
          </>
        )}
        { client && cashbackEnabled && (
          <View style={styles.cartTotalRowView}>
            <Text style={styles.cartTotalPriceLabel}>
              {t('cartView.bonusAccumulation') + ' - ' + resolveBonusLevel(client) + '%'}
            </Text>
            <Text style={styles.cartTotalPriceValue}>
              {currencyFormatter.format((sale ? sale.bonusAccumulation : bonusAccumulationValue(client, bonusAction, discountPercentage, discountAmount, cartItems, store, cashbackEnabled, effectivePriceModificators, punchCardEnabled, punchCardCapacity, priceType, priceTypes)))}
            </Text>
          </View>
        ) }
      </View>
    )}
    </Layout>
  )
}

function areEqual(prevProps, nextProps) {
  /*
  return true if passing nextProps to render would return
  the same result as passing prevProps to render,
  otherwise return false
  */
  if (prevProps.cartItems?.length !== nextProps.cartItems?.length) {
    return false
  }
  if (prevProps.discountPercentage !== nextProps.discountPercentage) {
    return false
  }
  if (prevProps.discountAmount !== nextProps.discountAmount) {
    return false
  }
  if (prevProps.client?.phoneNumber !== nextProps.client?.phoneNumber) {
    return false
  }
  if (prevProps.bonusAction !== nextProps.bonusAction) {
    return false
  }
  if (prevProps.client !== nextProps.client) {
    return false
  }
  if (prevProps.purchaseMethod?.id !== nextProps.purchaseMethod?.id) {
    return false
  }
  if (prevProps.cashbackEnabled !== nextProps.cashbackEnabled) {
    return false
  }
  if (prevProps.punchCardEnabled !== nextProps.punchCardEnabled) {
    return false
  }
  if (prevProps.punchCardCapacity !== nextProps.punchCardCapacity) {
    return false
  }
  if (prevProps.store !== nextProps.store) {
    return false
  }
  if (prevProps.priceType !== nextProps.priceType) {
    return false
  }
  // Only check cart items if they exist and have the same length
  if (prevProps.cartItems?.length > 0) {
    for (let index = 0; index < prevProps.cartItems.length; index++) {
      const prevElement = prevProps.cartItems[index]
      const nextElement = nextProps.cartItems[index]
      if (prevElement.quantity !== nextElement.quantity) {
        return false
      }
      if (prevElement.salePrices?.[prevProps.store] !== nextElement.salePrices?.[nextProps.store]) {
        return false
      }
      if (prevElement.itemDiscount !== nextElement.itemDiscount) {
        return false
      }
      if (prevElement.id !== nextElement.id) {
        return false
      }
    }
  }
  return true
}

export const CartView = React.memo(CartViewBase, areEqual)

const styles = StyleSheet.create({
  cart: {
    flex: 1,
    borderRadius: 5,
    justifyContent: 'flex-start',
    minHeight: 100,
    paddingVertical: 5,
  },
  cartItem: {
    margin: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  itemTitle: {
    width: '36%',
    flexWrap: 'wrap',
    fontSize: 13,
  },
  itemQuantityContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '32%',
  },
  itemQuantityField: {
    textAlign: 'center',
    fontSize: 13,
  },
  itemPriceContainer: {
    width: '32%',
  },
  itemDiscountField: {
    textAlign: 'right',
  },
  itemPriceField: {
    textAlign: 'right',
    fontSize: 13
  },
  cartTotalView: {
    margin: 8,
  },
  cartTotalRowView: {
    flexDirection: 'row',
  },
  cartTotalPriceLabel: {
    width: '36%',
    fontSize: 13
  },
  cartTotalPriceValue: {
    width: '64%',
    fontSize: 13,
    textAlign: 'right',
  },
  changeButton: {
    marginHorizontal: 3,
    padding: 5,
  },
})
